export const initialValues = {
  title: '',
  defaultIndex: '',
  style: 'default',
  lineStyle: 'linear',
  provider: 'typesense',
  // Sidebar filters render as collapsible <details> sections when true, else as
  // plain always-open blocks (the default).
  collapsibleFilters: false,
  // Footer stamped on downloaded chart images (PNG/SVG only — CSV has none).
  // Chart-wide: every group's download gets the same footer, since one chart
  // reads one collection and so has one source. The page URL and date are
  // filled in automatically at download time; these two are configurable.
  // Whether the Download control appears at all is per-group (group.toolbar).
  imageFooter: {
    source: 'NVIDIA',
    disclaimer: 'Measured performance at date of benchmark, subject to change. '
      + 'Actual results may vary by workload and system configuration.',
  },
  groups: [],
};

export const groupDefaults = {
  label: '',
  labelFields: '',
  description: '',
  type: 'line',
  groupByFields: '',
  groupLabelFormat: '',
  filterBy: '',
  axes: {
    x: {
      label: '',
      field: '',
      band: {
        mode: '',
        fields: ['', ''],
        label: '',
        // Gate: offer the band option only while the current rows carry this
        // field. Empty = always offered. Spelled out here so the key is visible
        // in every saved config rather than existing only in hand-edited JSON.
        showWhenField: '',
      },
    },
    y: {
      label: '',
      field: '',
    },
    // Extra selectable Y metrics beyond axes.y ([{ label, field }, ...]). The
    // switcher lists axes.y first (the default), then these; shown only when the
    // total is >1. Do not repeat axes.y here — yAxesOf merges them.
    yOptions: [],
  },
  tooltip: {
    title: '',
    text: '',
  },
  // Toolbar controls are opt-in per chart (off by default) so enabling them on
  // one chart doesn't add controls to every existing chart.
  toolbar: {
    viewToggle: false,
    download: false,
    share: false,
  },
  // Pareto frontier (opt-in). `frontierOnly` draws only each curve's
  // non-dominated points as the connected line; `showDominated` then adds the
  // off-frontier points back as hollow markers. showDominated depends on
  // frontierOnly — with it off every point is already drawn, so there is no
  // "dominated" set to distinguish. Off by default: non-Pareto charts keep the
  // connect-all-points line.
  pareto: {
    frontierOnly: false,
    showDominated: false,
  },
  filters: [],
  // Rule-based line styling. When non-empty this wins over colorsMap, which is
  // kept and consulted only for the combinations no rule matched. Presence is
  // the precedence mechanism — there is no mode key — mirroring the
  // tooltip.text / tooltip.advanced convention above. Absent or empty means the
  // chart resolves exactly as it did before rules existed.
  colorRules: [],
  colorsMap: [],
}

export const filterSourceDefaults = {
  label: '',
  attribute: '',
  kind: 'checkboxes',
  defaultValues: [],  // Array of default facet values to pre-select
};

export const lineColor = {
  id: '',
  color: '',
  lineStyle: 'solid',
  markerShape: 'circle',
  markerFill: 'filled',
};

// One `when` condition of a color rule. `when` is an ARRAY of these rather than
// a { field: pattern } object for three reasons: an object cannot hold two
// conditions on the same field, Formik's FieldArray needs a stable index to key
// rows on (keying by field name relocates a row the moment the dropdown
// changes, remounting the input mid-edit), and an array makes the order the
// author sees the order that is stored. All conditions of one rule are ANDed.
export const colorRuleCondition = {
  field: '',
  pattern: '',
};

// A color rule styles every line whose group fields match `when`, and carries
// all four style attributes — same defaults as a literal lineColor row, and the
// form requires every one of them. The resolver is deliberately more permissive
// than the editor: it treats an omitted attribute as falling through to the next
// matching rule, so a hand-imported JSON config can still layer a thin rule over
// the colors. Nothing in the UI produces that shape today.
export const colorRule = {
  id: '',
  label: '',
  when: [{...colorRuleCondition}],
  // Seeded, unlike lineColor's empty `color`, because a rule REQUIRES all four
  // style attributes: an empty seed makes a rule invalid the moment it is added,
  // so the author meets a validation error before touching anything. The swatch
  // lies about it too — <input type="color"> renders '' as black, so the row
  // shows a colour it does not have. Brand green is the palette's first entry.
  color: '#76b900',
  lineStyle: 'solid',
  markerShape: 'circle',
  markerFill: 'filled',
};


export const colorPalette = {
  "brandGreen": "#76b900",

  // Sub Color Palette (ordered by number, highest to lowest)
  "green700": "#265600",
  "green500": "#337a00",
  "green300Brand": "#76b900",
  "green100": "#b2e639",
  "blue800": "#002781",
  "blue700": "#0046a4",
  "blue500": "#0074df",
  "blue200": "#7cd7fe",
  "fuschia800": "#5d1337",
  "fuschia700": "#8c1c55",
  "fuschia500": "#d2308e",
  "fuschia300": "#fc79ca",
  "fuschia100": "#ffd3f2",
  "purple800": "#4d1368",
  "purple700": "#741d9d",
  "purple400": "#c359ef",
  "purple100": "#f9d4ff",
  "red700": "#961515",
  "red500": "#e52020",
  "red300": "#ff8181",
  "red100": "#ffd7d7",
  "teal700": "#04554b",
  "teal500": "#0d8473",
  "teal300": "#1dbba4",
  "teal100": "#9aefe5",
  "yellow400": "#df6500",
  "yellow300": "#ef9100",
  "yellow200": "#f9c500",
  "yellow100": "#fcde7b",
}

// The ordered color sequence the settings form's "generate" button hands out,
// one entry per unique data combination. It used to be `colorPalette` cycled
// five times, which meant a chart with more than 30 lines drew line 31 in the
// exact same color as line 1. 100 distinct entries covers the largest charts we
// publish today.
//
// Slots 1-30 are `colorPalette` in its existing order so charts generated
// before this change keep their assignments. The one exception is slot 4:
// `brandGreen` and `green300Brand` are both #76b900, so lines 1 and 4 always
// came out identical. That slot now carries a vivid mid-green instead — it is
// the only pre-existing assignment that moves.
//
// Slots 31-100 were derived offline in OKLCH (perceptually uniform, so equal
// steps look equal) rather than hand-picked:
//
//   - Hue stays inside the eight brand families already present in the palette
//     — green 131, orange 48, yellow 90, teal 180, blue 256, purple 313,
//     fuschia 350, red 25 degrees — with at most +/- 20 degrees of wiggle, so
//     nothing reads as an off-brand hue.
//   - Lightness spans 0.38-0.82 and chroma sits at 70-100% of the in-gamut
//     maximum. That band keeps every new color at >= 1.65:1 contrast on a white
//     page and >= 1.93:1 on a dark one, so no line vanishes under either
//     `<html data-color-scheme>` setting.
//   - Candidates were picked greedily by farthest-first search on CIEDE2000
//     distance from every color already in the list, capped at nine per family.
//     Nearest-neighbor distance across all 100 is >= 8.1 dE for the new colors
//     (the two closest colors overall, #ffd3f2 and #f9d4ff at 4.2 dE, are both
//     pre-existing palette entries and were left alone).
//   - The 70 are then interleaved green, red, blue, orange, purple, yellow,
//     teal, fuschia so consecutive lines land at least 90 degrees apart in hue.
//     Minimum adjacent distance in this stretch is 19.6 dE.
//
// Past 100 lines `generateColors` wraps and repeats from the top, which is the
// same behavior as before — just 100 lines later.
export const generatedColorSequence = [
  ...Object.values({...colorPalette, "green300Brand": "#26a900"}),
  "#224d2e", "#7f0027", "#154862", "#61351d", "#7400ee",
  "#49431e", "#224a4c", "#732b6f", "#555700", "#883500",
  "#1400e5", "#ae3e35", "#8142c1", "#745100", "#356e71",
  "#913247", "#357247", "#b54052", "#206588", "#9e5a36",
  "#ab00e4", "#6c6d33", "#00858a", "#9b3c95", "#00a14e",
  "#ce4b73", "#5348ff", "#bf3b00", "#815fd8", "#998700",
  "#4a9872", "#be0064", "#7d9044", "#e15854", "#007eb1",
  "#9b5f00", "#aa8eff", "#aa7c43", "#009c89", "#c735c0",
  "#abc45f", "#ff6641", "#707aff", "#cc6342", "#e578f3",
  "#c58d00", "#4d9a9e", "#ff3471", "#37e300", "#e67592",
  "#009edd", "#d58b50", "#dea1ff", "#c5a857", "#41bf86",
  "#e355a3", "#6fe08f", "#e7876e", "#9aa6e7", "#ffae84",
  "#c5bcee", "#b8b900", "#00d0d9", "#ff18ce", "#eda7b6",
  "#6fadff", "#ffab9f", "#eeb97a", "#6fdcb9", "#f7a6dc",
]
